import { apiRequest } from "./api.client";

export type PaymentMode = "cash" | "upi" | "card" | "cheque" | "bank_transfer" | "other";
export type RecurringFrequency = "weekly" | "monthly" | "quarterly" | "yearly";

export interface ExpenseCategory {
  category_id: string;
  clinic_id: string | null;
  name: string;
  code: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export interface Expense {
  expense_id: string;
  clinic_id: string;
  category_id: string;
  amount_paise: number;
  expense_date: string;
  vendor_name: string | null;
  payment_mode: PaymentMode | null;
  payment_reference: string | null;
  receipt_s3_key: string | null;
  notes: string | null;
  recurring_template_id: string | null;
  period_key: string | null;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ExpenseCreatePayload {
  category_id: string;
  amount_paise: number;
  expense_date: string;
  vendor_name?: string;
  payment_mode?: PaymentMode;
  payment_reference?: string;
  notes?: string;
}

export interface ExpenseListFilters {
  date_from?: string;
  date_to?: string;
  category_id?: string;
  vendor_name?: string;
  skip?: number;
  limit?: number;
}

export interface RecurringExpenseTemplate {
  template_id: string;
  clinic_id: string;
  category_id: string;
  amount_paise: number;
  vendor_name: string | null;
  payment_mode: PaymentMode | null;
  frequency: RecurringFrequency;
  day_of_period: number;
  start_date: string;
  end_date: string | null;
  next_run_date: string;
  is_active: boolean;
  notes: string | null;
  created_by: string;
  created_at: string;
}

export interface RecurringExpenseTemplateCreatePayload {
  category_id: string;
  amount_paise: number;
  vendor_name?: string;
  payment_mode?: PaymentMode;
  frequency: RecurringFrequency;
  day_of_period: number;
  start_date: string;
  end_date?: string;
  notes?: string;
}

function qs(params: object): string {
  const search = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(
    ([k, v]) => v !== undefined && v !== null && search.set(k, String(v))
  );
  const s = search.toString();
  return s ? `?${s}` : "";
}

export const ExpenseAPI = {
  listCategories: () => apiRequest<ExpenseCategory[]>("/expense-categories"),

  createCategory: (name: string, code: string) =>
    apiRequest<ExpenseCategory>("/expense-categories", {
      method: "POST",
      body: JSON.stringify({ name, code }),
    }),

  deactivateCategory: (categoryId: string) =>
    apiRequest<null>(`/expense-categories/${categoryId}`, { method: "DELETE" }),

  list: (filters: ExpenseListFilters = {}) =>
    apiRequest<Expense[]>(`/expenses${qs(filters)}`),

  get: (expenseId: string) => apiRequest<Expense>(`/expenses/${expenseId}`),

  create: (payload: ExpenseCreatePayload) =>
    apiRequest<Expense>("/expenses", { method: "POST", body: JSON.stringify(payload) }),

  update: (expenseId: string, payload: Partial<ExpenseCreatePayload>) =>
    apiRequest<Expense>(`/expenses/${expenseId}`, { method: "PUT", body: JSON.stringify(payload) }),

  delete: (expenseId: string) => apiRequest<null>(`/expenses/${expenseId}`, { method: "DELETE" }),

  getReceiptUploadUrl: (expenseId: string) =>
    apiRequest<{ upload_url: string | null; s3_key: string }>(`/expenses/${expenseId}/receipt-upload-url`),

  confirmReceipt: (expenseId: string, s3Key: string) =>
    apiRequest<Expense>(`/expenses/${expenseId}/receipt/confirm`, {
      method: "POST",
      body: JSON.stringify({ s3_key: s3Key }),
    }),

  getReceiptViewUrl: (expenseId: string) =>
    apiRequest<{ view_url: string }>(`/expenses/${expenseId}/receipt-url`),

  // ── Recurring templates ─────────────────────────────────────────────────

  listRecurringTemplates: () => apiRequest<RecurringExpenseTemplate[]>("/recurring-expenses"),

  createRecurringTemplate: (payload: RecurringExpenseTemplateCreatePayload) =>
    apiRequest<RecurringExpenseTemplate>("/recurring-expenses", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateRecurringTemplate: (templateId: string, payload: Partial<RecurringExpenseTemplateCreatePayload> & { is_active?: boolean }) =>
    apiRequest<RecurringExpenseTemplate>(`/recurring-expenses/${templateId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  deleteRecurringTemplate: (templateId: string) =>
    apiRequest<null>(`/recurring-expenses/${templateId}`, { method: "DELETE" }),
};

export default ExpenseAPI;
